import IJobSection from "../../interfaces/IJobSection";
import { BoxHeader } from "./box-header";
import * as React from "react";
import { useMemo, useState } from "react";
import Icons from "../Icons";
import InhibitorTable from "../inhibitor-table/InhibitorTable";
import { BoxHeaderOption } from "./box-header-option";
import { tooltip } from "../../../../utils/tooltip";
import { useResultsContext } from "../../context/results-context";
import { Section } from "./section";
import { VinaJobCheckedContainer } from "./vina-job-checked-container";

interface Props {
  section: IJobSection,
  scrollToInhibitorChart: () => void,
}

export function SectionInhibitorList({section, scrollToInhibitorChart}: Props) {

  // Constants:
  const onlyOtherAssocDrugs = true;

  // Context:

  const {
    results, uniprot,
    selectedInhibitor, setSelectedInhibitor,
    inhibitorDiffFilter, setInhibitorDiffFilter
  } = useResultsContext();

  const {inhibitors} = results;

  // State:

  const [inhibitorOnlyAssociatedDrugs, setInhibitorOnlyAssociatedDrugs] = useState(false);

  // Memo:

  const associatedDrugs = useMemo(() => {
    let out = uniprot.associatedDrugs ?? [];
    if (onlyOtherAssocDrugs && inhibitors) {
      // we want to display only OTHER associated drugs (which are not in the table)
      // get set of IDs of associated drugs in the table
      const inhibitorsOfAssociatedDrugs = inhibitors.filter(inh => inh.associatedDrug && inh.drugbankId !== null && inh.drugbankId !== "");
      const idSet = new Set(inhibitorsOfAssociatedDrugs.map(inh => inh.drugbankId));
      // keep only drugs which are not the table
      out = out.filter(assocDrug => !idSet.has(assocDrug.id));
    }
    return out;
  }, [uniprot.associatedDrugs, onlyOtherAssocDrugs, inhibitors]);

  // Render section:

  return <Section section={section} className="ll-break-inside-avoid" style={{marginTop: '1rem'}}>
    <BoxHeader section={section}>
      <BoxHeaderOption
        title={<>only associated drugs {Icons.associatedDrug}</>}
        checked={inhibitorOnlyAssociatedDrugs}
        setChecked={setInhibitorOnlyAssociatedDrugs}
        labelClassName={'ll-screen'}
      />
      <span className="ll-print">{Icons.associatedDrug} associated drug</span>
    </BoxHeader>
    <div className="ll-section-body">
      <VinaJobCheckedContainer>
        <div style={{ marginTop: '0.5rem' }}>
          <InhibitorTable
            inhibitors={inhibitors}
            showOnlyAssociated={inhibitorOnlyAssociatedDrugs}
            differenceFilter={inhibitorDiffFilter}
            selected={selectedInhibitor}
            setDifferenceFilter={setInhibitorDiffFilter}
            setSelected={setSelectedInhibitor}
            scrollToChart={scrollToInhibitorChart}
          />
          {associatedDrugs !== null && associatedDrugs.length > 0 && (
            <div className="ll-drug-annotations">
              <h3>
                <span>{onlyOtherAssocDrugs ? 'Other associated drugs' : 'Associated drugs'}</span>
                {onlyOtherAssocDrugs && tooltip('other_associated_drugs')}
              </h3>
              {associatedDrugs.map(({ id, name }) => <p key={id}>
                <a href={`https://go.drugbank.com/drugs/${id}`} target="_blank">{id}</a>, {name}
                {Icons.associatedDrug}
              </p>)}
            </div>
          )}
          {uniprot.resistance !== null && uniprot.resistance.length > 0 && (
            <div className="ll-drug-annotations">
              <h3>
                <span>Resistance</span>
                {tooltip('resistance')}
              </h3>
              {uniprot.resistance.map(({ id, name }) => <p key={id}>
                <a href={`https://www.reactome.org/PathwayBrowser/#${id}&FLG=${results.uniprotAccession}`} target="_blank">{id}</a>, {name}
              </p>)}
            </div>
          )}

        </div>
      </VinaJobCheckedContainer>
    </div>
  </Section>
}